import {
  ApiFormationsEventIdGet200Response,
  ApiFormationsEventIdGet200ResponseDataSubstitutionsHomeInner,
} from '@/api/livescore/generated';
import LivescoreEntityLink from '@/components/atoms/LivescoreEntityLink/LivescoreEntityLink';
import LivescorePlayerStats from '@/components/atoms/LivescorePlayerStats/LivescorePlayerStats';
import Substitution from '@/components/icons/Substitution';
import { useLivescoreMatch } from '@/context/LivescoreMatchContext';
import { useWindowSize } from '@/hooks/useWindowResize';
import { cn } from '@/utils/cn';
import Image from 'next/image';
import React from 'react';

const SubstitiutionItem = ({
  sub,
  reverse,
}: {
  sub: ApiFormationsEventIdGet200ResponseDataSubstitutionsHomeInner;
  reverse?: boolean;
}) => {
  const { match } = useLivescoreMatch();
  const { width } = useWindowSize();

  const timeDiff = new Date(sub.time).getTime() - new Date(match.sport_event.start_time || 0).getTime();
  const minutes = Math.floor(timeDiff / 60000);

  return (
    <li className={cn('flex items-center gap-2', reverse ? 'flex-row-reverse' : '')}>
      <div className="w-[28px] h-[28px] sm:w-[30px] sm:h-[30px] relative rounded-full overflow-hidden mb-2">
        <Image src={sub.to.img ?? '/images/player.png'} alt={sub.to.name} layout="fill" objectFit="contain" />
      </div>
      <div className="space-y-1 sm:space-y-0.5">
        <LivescoreEntityLink
          href={`/livescore/player/${sub.to.slug ? sub.to.slug : sub?.to?.id?.split(':')[2]}`}
          className={cn('flex items-center gap-2', reverse ? 'flex-row-reverse' : '')}
        >
          <p className="text-[7px] sm:text-xxs">{sub.to.name}</p>
          <LivescorePlayerStats
            stats={
              match.statistics?.totals?.competitors
                ?.find((c) => c.qualifier === 'home')
                ?.players.find((p) => p.id === sub.to.id)?.statistics ?? {}
            }
            position={sub.to.type ?? ''}
            className="text-[6px] sm:text-[9px] !text-white"
          />
        </LivescoreEntityLink>
        <LivescoreEntityLink
          href={`/livescore/player/${sub.from.slug ? sub.from.slug : sub?.from?.id?.split(':')[2]}`}
          className={cn('flex items-center gap-1', reverse ? 'flex-row-reverse' : '')}
        >
          <Substitution width={width ? (width > 600 ? 12 : 7) : 7} height={width ? (width > 600 ? 10 : 5.33) : 5.33} />
          <p className="text-[7px] font-semibold sm:text-xxs">{sub.from.name}</p>
          <span className="dark:bg-black-500 bg-white rounded border dark:border-border-400 border-gray-250 px-[3px] py-[1px] sm:px-1 sm:py-0.5">
            <p className="text-[7px] sm:text-[8px] font-semibold">{minutes > 45 ? minutes - 15 : minutes}’</p>
          </span>
        </LivescoreEntityLink>
      </div>
    </li>
  );
};

const Substitutions = () => {
  const { match, substitutions } = useLivescoreMatch();

  if (!substitutions?.home.length && !substitutions?.away.length) {
    return null;
  }

  return (
    <>
      <h3 className="text-black dark:text-white font-semibold text-xs mb-2.5">Zmienieni zawodnicy</h3>
      <div className="flex justify-between dark:bg-black-400 bg-white_theme-50 text-black dark:text-white rounded-md mt-2 px-2 sm:px-5 py-3">
        <div>
          <h4 className="font-semibold text-xxs sm:text-xs mb-2">
            {match.sport_event.competitors.find((c) => c.qualifier === 'home')?.name}
          </h4>
          <ul className="space-y-3 pl-0">
            {substitutions?.home.map((substitution) => (
              <SubstitiutionItem key={substitution.to.id} sub={substitution} />
            ))}
          </ul>
        </div>
        <div className="flex flex-col items-end">
          <h4 className="font-semibold text-xxs sm:text-xs mb-2">
            {match.sport_event.competitors.find((c) => c.qualifier === 'away')?.name}
          </h4>
          <ul className="flex flex-col items-end space-y-3 pl-0">
            {substitutions?.away.map((substitution) => (
              <SubstitiutionItem key={substitution.to.id} sub={substitution} reverse />
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default Substitutions;
